import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, Loader2, ArrowRight, Compass } from "lucide-react";
import { toast } from "sonner";

const GOALS = ["Get a job", "Upskill at work", "Learn a new language", "Start a business", "Personal growth"];

export default function LearningPath() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    topic: "", level: "Beginner", language: "English", goal: "Personal growth", time_available_hours: 4,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.topic.trim()) { toast.error("Tell us what you want to learn"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/api/learning-path/generate", form);
      toast.success("Your learning path is ready!");
      nav(`/courses/${data.slug}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate path");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-16">
      <div className="grid md:grid-cols-2 gap-12 items-start">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Compass className="w-3 h-3 text-[#D92D20]" /> Personalized learning</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-3 tracking-tight">Create your learning path.</h1>
          <p className="mt-4 text-[#4B5563] leading-relaxed">
            Tell us what you want to learn, your level and the time you have — and we'll craft a tailored
            mini-course preview just for you. Try a free lesson and unlock the rest when you're ready.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-[#4B5563]">
            <li>• Built around your topic and goal</li>
            <li>• English or French</li>
            <li>• Free preview of the first lesson</li>
            <li>• Pay once to unlock all modules + certificate</li>
          </ul>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#E5E7EB] rounded-2xl p-6 md:p-8 space-y-5" data-testid="learning-path-form">
          <div>
            <label className="text-sm font-semibold">What do you want to learn?</label>
            <input data-testid="lp-topic" value={form.topic} onChange={(e) => setForm({...form, topic: e.target.value})}
              placeholder="e.g. Customer service for retail jobs"
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold">Skill level</label>
              <select data-testid="lp-level" value={form.level} onChange={(e) => setForm({...form, level: e.target.value})}
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
                <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold">Language</label>
              <select data-testid="lp-language" value={form.language} onChange={(e) => setForm({...form, language: e.target.value})}
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
                <option>English</option><option>French</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold">Your main goal</label>
            <select data-testid="lp-goal" value={form.goal} onChange={(e) => setForm({...form, goal: e.target.value})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white">
              {GOALS.map((g) => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold">Time available (hours)</label>
            <input data-testid="lp-time" type="number" min={1} max={20} value={form.time_available_hours}
              onChange={(e) => setForm({...form, time_available_hours: parseFloat(e.target.value)})}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
          </div>
          <button data-testid="lp-submit" type="submit" disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Create my path <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-xs text-[#4B5563]">It can take up to 30 seconds to generate your personalized course.</p>
        </form>
      </div>
    </div>
  );
}
